import { Component, input, OnChanges, OnDestroy, output, SimpleChanges, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Observable } from 'rxjs';

import { BaasConfirmOptions } from '../baas-confirm/baas-confirm.types';
import { BarEditPageLoadArgs } from './bar-edit-page-load-args.model';
import { BarEditPageSaveArgs } from './bar-edit-page-save-args.model';
import { BarEditPageSaveOptions } from './bar-edit-page-save-options.model';
import { BarEditPageWrapperComponent } from './wrapper/bar-edit-page-wrapper.component';

@Component({
    templateUrl: './bar-edit-page.html',
    selector: 'bar-edit-page',
    imports: [BarEditPageWrapperComponent]
})
/** @deprecated Use BaasEdit in combination with BaasLayout (OnPush). Keep using this component if you are using the legacy layout.
 * Edit page component for pages with edit actions and autosave support. */
export class BarEditPage implements OnChanges, OnDestroy {
    /** The internal wrapper used to be backwards compatible with the legacy system. */
    readonly wrapper = viewChild(BarEditPageWrapperComponent);
    /** Callback that allows to run something before saving and to prevent the save from happen. */
    readonly saving = input<() => void | boolean | Observable<void | boolean>>(undefined);
    /** Request/response callback that occurs when saving. Here we need to persist the data. */
    readonly save = input<(args: BarEditPageSaveArgs<any>) => Observable<any | boolean | void>>(undefined);
    /** Observable to indicate that the save request has been completed successfully at the consumer. */
    readonly saveSuccess$ = input<Observable<any | boolean | void>>(undefined);
    /** Observable to indicate that the save request has failed at the consumer. */
    readonly saveFailed$ = input<Observable<Error>>(undefined);
    /** Callback that occurs whenever data needs to be loaded (first time, after save, after undo...) */
    readonly load = input<(args: BarEditPageLoadArgs) => void | Observable<void>>(undefined);
    /** Request/response callback that performs the delete. */
    readonly delete = input<() => Observable<boolean | void>>(undefined);
    /** Callback indicating whether delete was completed successfully by the consumer. */
    readonly deleteSuccess$ = input<Observable<boolean | void>>(undefined);
    /** Callback indicating whether delete failed due to an error by the consumer. */
    readonly deleteFailed$ = input<Observable<Error>>(undefined);
    /** Callback that performs extra undo functionality. */
    readonly undo = input<() => Observable<void> | void>(undefined);
    /** The data retrieved from back-end.  */
    readonly data = input<any>(undefined);
    /** Observable with the model. */
    readonly model$ = input<Observable<any>>(undefined);
    readonly form = input<FormGroup>(undefined);
    readonly disabled = input<boolean>(undefined);
    /** Whether the page is in readonly. This means we don't have save/undo/delete buttons. */
    readonly readonly = input(false);
    /** Whether the item in the page is deletable in case in edit modus. A delete button will appear. */
    readonly deletable = input(false);
    /** If an edit page is disabled or readonly and deletable the delete button doesn't show. With this property we force the delete button to appear in all cases. */
    readonly forceDeletable = input(false);
    /** Whether the item in the page is saveable in case in edit modus. If true (default) the save and undo buttons appear. */
    readonly saveable = input(true);
    /** Whether editable. If not defined it is immediately editable if not readonly.
     * If readonly == true && editable == true then an edit button appears. */
    readonly editable = input<boolean>(null);
    /** Whether to automatically define the save model. It will be filled in the model property of BarEditPageSaveArgs. */
    readonly autoGenerateSaveModel = input(true);
    /** Whether the undo action must be confirmed */
    readonly confirmUndo = input(false);
    /** Whether the save action must be confirmed */
    readonly confirmSave = input(false);
    /** Whether to show an undo message after doing the undo. */
    readonly showUndoMessage = input(false);
    /** Whether to show a delete message after doing the delete. */
    readonly showDeleteMessage = input(false);
    /** Whether to show a save message after doing a save. */
    readonly showSaveMessage = input(true);
    /** Whether to force to be in valid modus even if the form is invalid */
    readonly forceValid = input(false);
    /** Whether to force that the edit button is visible even if edit disabled in case using the editable flag */
    readonly forceEditActionVisible = input(false);
    /** Whether the form must be valid in case the form is pristine.
     * The default behavior is false: In case pristine no validation is done. */
    readonly enforceValidOnPristine = input(false);
    /** Whether the before unload event, in case the user navigates away (href), is disabled. */
    readonly beforeUnloadDisabled = input(false);
    /** Whether to show an extra warning when the form is invalid and a save occurs. */
    readonly showSaveMessageOnInvalid = input(true);
    /** Whether loading progress bar should appear or not. Use this in modelless modus where no model is used. */
    readonly loading = input<boolean>(undefined);
    /** Content to show in the edit button. */
    readonly editContent = input<string>(undefined);
    /** Tooltip on the delete button. */
    readonly deleteTooltip = input<string>(undefined);
    /** Tooltip on the save button */
    readonly saveTooltip = input<string>(undefined);
    /** Content to show in the save button. */
    readonly saveContent = input<string>(undefined);
    /** Tooltip on the edit button. */
    readonly editTooltip = input<string>(undefined);
    /** Tooltip on the undo button. */
    readonly undoTooltip = input<string>(undefined);
    /** Content to show in the undo button. */
    readonly undoContent = input<string>(undefined);
    /** Delete success message. */
    readonly deleteMessage = input<string>(undefined);
    /** Content to show in the delete button. */
    readonly deleteContent = input<string>(undefined);
    /** Save success message. */
    readonly saveMessage = input<string>(undefined);
    /** Undo performed message */
    readonly undoMessage = input<string>(undefined);
    /** @deprecated Use confirmDeleteOptions property. Message within delete confirmation. */
    readonly confirmDeleteMessage = input<string>(undefined);
    /** Optional options for the delete confirmation dialog.  */
    readonly confirmDeleteOptions = input<BaasConfirmOptions>(undefined);
    /** @deprecated Use confirmDeleteOptions property. Message within undo confirmation. */
    readonly confirmUndoMessage = input<string>(undefined);
    /** Optional options for the undo confirmation dialog.  */
    readonly confirmUndoOptions = input<BaasConfirmOptions>(undefined);
    /** @deprecated Use confirmDeleteOptions property. Message within save confirmation. */
    readonly confirmSaveMessage = input<string>(undefined);
    /** Optional options for the save confirmation dialog.  */
    readonly confirmSaveOptions = input<BaasConfirmOptions>(undefined);
    readonly submitted = input<boolean>(undefined);
    readonly submittedChange = output<boolean>();
    /** Event that occurs that a save needs to occur at the consumer after validation checks. */
    readonly saveRequested = output<BarEditPageSaveArgs<any>>();
    /** Event that occurs when the save is completed and has been executed. Some code can be executed at the consumer. */
    readonly saveCompleted = output<BarEditPageSaveArgs<any>>();
    /** Event that occurs that a delete needs to occur at the consumer. */
    readonly deleteRequested = output<void>();
    /** Event that occurs when an undo is requested. Use this to perform custom undo logic. */
    readonly undoRequested = output<void>();
    /** Event that occurs when the edit button (pencil) is pressed. Use this to perform extra logic. */
    readonly editRequested = output<void>();

    ngOnChanges(changes: SimpleChanges): void {
        // NOTE this won't be needed once the bar-edit-page-base.component is gone
        // use timeout to be sure wrapper is initialized and to avoid value has changes after it was checked
        window.setTimeout(() => this.#applyChanges(changes));
    }

    ngOnDestroy(): void {}

    /** Requests a save returning false in case invalid. */
    requestSave(input: BarEditPageSaveOptions): Observable<boolean> {
        return this.wrapper().requestSave(input);
    }

    /** Requests a delete  */
    requestDelete(): Observable<boolean> {
        return this.wrapper().delete();
    }

    /** Requests an undo. */
    requestUndo(): Observable<void> {
        return this.wrapper().undo();
    }

    #applyChanges(changes: SimpleChanges): void {
        for (const propName in changes) {
            const change = changes[propName];
            switch (propName) {
                // here we pass input fields values to ordinary fields in the wrapper
                case 'disabled': {
                    this.wrapper().disabled = change.currentValue;
                    break;
                }
                case 'readonly': {
                    this.wrapper().readonly = change.currentValue;
                    break;
                }
                case 'deletable': {
                    this.wrapper().deletable = change.currentValue;
                    break;
                }
                case 'forceDeletable': {
                    this.wrapper().forceDeletable = change.currentValue;
                    break;
                }
                case 'saveable': {
                    this.wrapper().saveable = change.currentValue;
                    break;
                }
                case 'editable': {
                    this.wrapper().editable = change.currentValue;
                    break;
                }
                case 'editContent': {
                    this.wrapper().editContent = change.currentValue;
                    break;
                }
                case 'autoGenerateSaveModel': {
                    this.wrapper().autoGenerateSaveModel = change.currentValue;
                    break;
                }
                case 'confirmUndo': {
                    this.wrapper().confirmUndo = change.currentValue;
                    break;
                }
                case 'confirmSave': {
                    this.wrapper().confirmSave = change.currentValue;
                    break;
                }
                case 'showUndoMessage': {
                    this.wrapper().showUndoMessage = change.currentValue;
                    break;
                }
                case 'showDeleteMessage': {
                    this.wrapper().showDeleteMessage = change.currentValue;
                    break;
                }
                case 'showSaveMessage': {
                    this.wrapper().showSaveMessage = change.currentValue;
                    break;
                }
                case 'forceValid': {
                    this.wrapper().forceValid = change.currentValue;
                    break;
                }
                case 'forceEditActionVisible': {
                    this.wrapper().forceEditActionVisible = change.currentValue;
                    break;
                }
                case 'enforceValidOnPristine': {
                    this.wrapper().enforceValidOnPristine = change.currentValue;
                    break;
                }
                case 'beforeUnloadDisabled': {
                    this.wrapper().beforeUnloadDisabled = change.currentValue;
                    break;
                }
                case 'showSaveMessageOnInvalid': {
                    this.wrapper().showSaveMessageOnInvalid = change.currentValue;
                    break;
                }
                case 'deleteTooltip': {
                    this.wrapper().deleteTooltip = change.currentValue;
                    break;
                }
                case 'deleteContent': {
                    this.wrapper().deleteContent = change.currentValue;
                    break;
                }
                case 'saveTooltip': {
                    this.wrapper().saveTooltip = change.currentValue;
                    break;
                }
                case 'saveContent': {
                    this.wrapper().saveContent = change.currentValue;
                    break;
                }
                case 'editTooltip': {
                    this.wrapper().editTooltip = change.currentValue;
                    break;
                }
                case 'undoTooltip': {
                    this.wrapper().undoTooltip = change.currentValue;
                    break;
                }
                case 'undoContent': {
                    this.wrapper().undoContent = change.currentValue;
                    break;
                }
                case 'deleteMessage': {
                    this.wrapper().deleteMessage = change.currentValue;
                    break;
                }
                case 'saveMessage': {
                    this.wrapper().saveMessage = change.currentValue;
                    break;
                }
                case 'undoMessage': {
                    this.wrapper().undoMessage = change.currentValue;
                    break;
                }
                case 'confirmDeleteMessage': {
                    this.wrapper().confirmDeleteMessage = change.currentValue;
                    break;
                }
                case 'confirmDeleteOptions': {
                    this.wrapper().confirmDeleteOptions = change.currentValue;
                    break;
                }
                case 'confirmUndoMessage': {
                    this.wrapper().confirmUndoMessage = change.currentValue;
                    break;
                }
                case 'confirmUndoOptions': {
                    this.wrapper().confirmUndoOptions = change.currentValue;
                    break;
                }
                case 'confirmSaveMessage': {
                    this.wrapper().confirmSaveMessage = change.currentValue;
                    break;
                }
                case 'confirmSaveOptions': {
                    this.wrapper().confirmSaveOptions = change.currentValue;
                    break;
                }
                case 'loading': {
                    this.wrapper().showLoading = change.currentValue;
                    break;
                }
                case 'submitted': {
                    this.wrapper().setSubmitted(change.currentValue);
                    break;
                }
            }
        }
    }
}
